import { useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { FormattedMessage, useIntl } from 'react-intl'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import BarCodeComponent from 'components/Pages/ExperienceVoucher/BarCode'
import config from 'constants/config'
import { rem } from 'polished'
import { isolatePhoneNumber } from 'lib/format/formatPhoneNumber'

const TicketSection = styled.div`
  width: 100%;
  margin: ${rem(16)} 0;
`

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${rem(8)};
  border-bottom: ${rem(2)} solid ${props => props.theme.palette.neutral.control.black100};
  margin-bottom: ${rem(16)};
`

const TicketRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${rem(16)};

  &:last-child {
    margin-bottom: 0;
  }
`

const InstructionsList = styled.ol`
  margin: ${rem(16)} 0;
  padding-inline-start: ${rem(24)};

  li {
    margin-bottom: ${rem(12)};
    line-height: 1.4;
  }
`

const BulletList = styled.ul`
  margin: ${rem(8)} 0;
  padding-inline-start: ${rem(20)};

  li {
    margin-bottom: ${rem(6)};
    line-height: 1.4;
  }
`

const InfoSection = styled.div`
  margin-bottom: ${rem(16)};

  > * {
    margin-bottom: ${rem(4)};

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const PageContainer = styled.div`
  width: 100%;
`

const ContentContainer = styled.div`
  width: 100%;
`

const TicketWindowInstructionsRow = styled.div`
  display: flex;
  width: 100%;
  gap: ${rem(16)};
  align-items: flex-start;

  @media print {
    break-inside: avoid;
    break-inside: avoid-page;
    page-break-inside: avoid;
  }
`

const BarcodePositionContainer = styled.div`
  width: ${rem(80)};
  height: ${rem(310)};
  flex-shrink: 0;
  margin-inline-start: ${rem(-30)};
  display: flex;
  align-items: center;
  justify-content: center;

  @media print {
    /*
      The barcode is rotated (transform doesn't affect layout sizing), so the visual height can exceed the
      container and overlap the content below when printing. Reserve enough vertical space for the
      4-inch max barcode width (≈384px @ 96DPI) once rotated.
    */
    height: ${rem(420)};
    break-inside: avoid;
    break-inside: avoid-page;
    page-break-inside: avoid;
  }
`

const DisneyBarcodeContainer = styled.div`
  max-width: ${rem(384)}; /* 4 inches at 96 DPI */
  max-height: ${rem(48)};  /* 0.5 inches at 96 DPI */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* Disney requirement: Barcode must fit within 4"-by-½" rectangle with minimum 24-point font */

  @media print {
    break-inside: avoid;
    break-inside: avoid-page;
    page-break-inside: avoid;
  }
`

const RotatedBarcodeWrapper = styled.div`
  transform: rotate(90deg);
  transform-origin: center center;

  @media print {
    break-inside: avoid;
    break-inside: avoid-page;
    page-break-inside: avoid;
  }
`
interface VoucherProps {
  address: string
  expiryDate: string
  voucher: App.ExperienceVoucher
}

type VoucherCodeProps = {
  number: string
  pin: string | undefined
}

export function PrintDisneyOrlandoVoucher(props: VoucherProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { voucher } = props
  const supportPhoneNumber = isolatePhoneNumber(config.PRIORITY_SUPPORT_NUMBER_SUPPORT_ASSISTANT)
  const defaultTicketName = intl.formatMessage({
    id: 'JkFSspfq0WD7JYntr5Be',
    defaultMessage: 'Walt Disney World Theme Park Tickets',
    description: 'Fallback product name shown on the Disney Orlando voucher when a ticket fare type is unavailable',
  })

  const voucherCode = useMemo((): VoucherCodeProps => {
    return { number: voucher.providerItemId || '', pin: undefined }
  }, [voucher])

  const totalAdults = useMemo(() => {
    if (!voucher.ticketsInfo) return 0
    return voucher.ticketsInfo.reduce((total, ticket) => {
      // Check if ticket is for adults (Ages 10+, Adult, etc.)
      const isAdultTicket = ticket.fareType?.toLowerCase().includes('adult')
      return total + (isAdultTicket ? ticket.quantity : 0)
    }, 0)
  }, [voucher.ticketsInfo])

  const totalChildren = useMemo(() => {
    if (!voucher.ticketsInfo) return 0
    return voucher.ticketsInfo.reduce((total, ticket) => {
      // Check if ticket is for children (Ages 3-9, Child, etc.)
      const isChildTicket = ticket.fareType?.toLowerCase().includes('child')
      return total + (isChildTicket ? ticket.quantity : 0)
    }, 0)
  }, [voucher.ticketsInfo])

  const ticketValidityPeriod = useMemo(() => {
    if (!voucher.ticketsInfo || voucher.ticketsInfo.length === 0) return ''

    const firstTicket = voucher.ticketsInfo[0]
    const startDate = firstTicket.startDate
    const endDate = firstTicket.endDate

    if (!startDate || !endDate) return ''

    const start = moment(startDate)
    const end = moment(endDate)
    const daysDifference = end.diff(start, 'days') + 1 // +1 to include both start and end days

    const startFormatted = dateFormatter(start, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const endFormatted = dateFormatter(end, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })

    return intl.formatMessage(
      {
        id: '3LyF7-MBUpWf26RTPKXl',
        defaultMessage: 'Your ticket is valid any {daysDifference} days between {startFormatted} and {endFormatted}.',
        description: 'Ticket validity period text on Disney Orlando voucher',
      },
      { daysDifference, startFormatted, endFormatted },
    )
  }, [voucher.ticketsInfo, intl, dateFormatter])

  const allTicketProducts = useMemo(() => {
    const products: Array<{ name: string; quantity: number; details?: string }> = []

    if (voucher.ticketsInfo) {
      voucher.ticketsInfo.forEach(ticket => {
        const ticketName = ticket.fareType || defaultTicketName
        const existingProduct = products.find(p => p.name === ticketName)
        if (existingProduct) {
          existingProduct.quantity += ticket.quantity
        } else {
          products.push({
            name: ticketName,
            quantity: ticket.quantity,
            details: ticketValidityPeriod,
          })
        }
      })
    }

    return products
  }, [defaultTicketName, voucher, ticketValidityPeriod])

  return (
    <LayoutContainer variant="large">
      <PageContainer>
        <ContentContainer>
          <Group direction="vertical" gap={16}>
            {/* Header Section */}
            <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
              <Heading variant="heading4">
                {intl.formatMessage({
                  id: 'sswUdY2a8Oyu6RLe-1m-',
                  defaultMessage: 'TICKET CONFIRMATION',
                  description: 'Heading for Disney Orlando ticket confirmation voucher',
                })}
              </Heading>
              <Group direction="vertical" horizontalAlign="center">
                <DisneyBarcodeContainer>
                  <BarCodeComponent width={2.2} height={48} format="CODE39" displayValue value={voucherCode.number} />
                </DisneyBarcodeContainer>
              </Group>
            </Group>

            {/* Travel Distributor Information */}
            <InfoSection>
              <BodyText variant="small" weight="bold">{config.brandConfig.name}</BodyText>
              <BodyText variant="small"><strong>{intl.formatMessage({
                id: 'NGTkCUDmmWIi5HSbV_Bh',
                defaultMessage: 'Phone:',
                description: 'Form field label for phone number on Disney Orlando voucher',
              })}
              </strong> {supportPhoneNumber}
              </BodyText>
              <BodyText variant="small"><strong>{intl.formatMessage({
                id: 'dQlCgsLU7FiR-Uoo7val',
                defaultMessage: 'Reference #:',
                description: 'Label for reference number on Disney Orlando voucher',
              })}
              </strong> {voucher.orderNumber}
              </BodyText>
              <BodyText variant="small"><strong>{intl.formatMessage({
                id: 'xZz_rrFEzPprnKVHh63P',
                defaultMessage: 'Guests Name:',
                description: 'Form field label for guest name on Disney Orlando voucher',
              })}
              </strong> {voucher.customer?.name} {voucher.customer?.surname}
              </BodyText>
              <BodyText variant="small">
                {intl.formatMessage(
                  {
                    id: 'jDr_27FX-f7Vys28pt6b',
                    defaultMessage: '{totalAdults} of Adults Ages 10+, {totalChildren} of Children Ages 3-9',
                    description: 'Guest age breakdown showing adult and child counts on Disney Orlando voucher',
                  },
                  { totalAdults, totalChildren },
                )}
              </BodyText>
              <BodyText variant="small"><strong>{intl.formatMessage({
                id: 'WuStlR_b_pCbBBlh6vw6',
                defaultMessage: 'Disney Reservation #:',
                description: 'Label for Disney reservation number on Disney Orlando voucher',
              })}
              </strong> {voucherCode.number}
              </BodyText>
            </InfoSection>

            {/* Ticket Products Section */}
            <TicketSection>
              <TicketHeader>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: '4gm9hy7IRkYuARDDrwks',
                    defaultMessage: 'TICKET PRODUCTS',
                    description: 'Section heading for ticket products on Disney Orlando voucher',
                  })}
                </BodyText>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'FXl2epeVd-iXKGOO4S2e',
                    defaultMessage: 'Quantity',
                    description: 'Column heading for ticket quantity on Disney Orlando voucher',
                  })}
                </BodyText>
              </TicketHeader>

              {allTicketProducts.map((product, index) => (
                <TicketRow key={index}>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="small" weight="bold">{product.name}</BodyText>
                    {product.details && (
                      <BodyText variant="small">
                        {product.details}
                      </BodyText>
                    )}
                  </Group>
                  <BodyText variant="small" weight="bold">{product.quantity}</BodyText>
                </TicketRow>
              ))}
            </TicketSection>

            {/* Instructions Section */}
            <Group direction="vertical" gap={8} horizontalAlign="start">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'PnwCuVNwt9kAuLjzp1Vc',
                  defaultMessage: 'Follow these steps to use your ticket and save time at the gate:',
                  description: 'Instructions heading for ticket usage steps on Disney Orlando voucher',
                })}
              </BodyText>

              <InstructionsList>
                <li>
                  <BodyText variant="small">
                    <FormattedMessage
                      id="UuwGZSVeadV2oCtKnH3o"
                      defaultMessage="Visit <em>My Disney Experience</em> and link your ticket confirmation to your <em>My Disney Experience</em> account - StartYourDisneyExperience.com"
                      description="Step 1 instruction to link ticket to My Disney Experience account on Disney Orlando voucher"
                      values={{ em: (chunks) => <em>{chunks}</em> }}
                    />
                  </BodyText>
                </li>
                <li>
                  <BodyText variant="small">
                    <FormattedMessage
                      id="nfbP6DU0KwG8Fh4ZPMhm"
                      defaultMessage="<bold>Based upon the date(s) of your visit and reservation type, you may be required to make Theme Park reservations to enter a Theme Park.</bold> {br}If your ticket requires Theme Park reservations, make them immediately after receiving this ticket confirmation. Theme Park reservations are subject to availability. Contact your travel distributor or visit World.com/ThemeParkReservations to learn if your ticket requires Theme Park reservations."
                      description="Step 2 instruction about Theme Park reservations on Disney Orlando voucher"
                      values={{
                        bold: (chunks) => <strong>{chunks}</strong>,
                        br: <br />,
                      }}
                    />
                  </BodyText>
                </li>
                <li>
                  <BodyText variant="small">
                    <FormattedMessage
                      id="vW3-OtlvJ5ajLZ1NzTq2"
                      defaultMessage="Use Disney MagicMobile service to access your ticket from your smartphone’s digital wallet upon arrival at <em>Walt Disney World®</em> Resort OR visit a Theme Park ticket window."
                      description="Step 3 instruction to use MagicMobile or visit ticket window on Disney Orlando voucher"
                      values={{ em: (chunks) => <em>{chunks}</em> }}
                    />
                  </BodyText>
                </li>
              </InstructionsList>

              <InfoSection>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'q6fD9ub4mZbT8sQ3Dk1-',
                    defaultMessage: 'Disney MagicMobile Service (Recommended)',
                    description: 'Heading for Disney MagicMobile service section on Disney Orlando voucher',
                  })}
                </BodyText>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'eUUPK17Vrv1LxlSpH4zW',
                    defaultMessage: 'Skip the wait at the ticket window! (Some tickets may still require you to visit a ticket window for activation.) Most tickets can be accessed using the digital wallet on an eligible iPhone®, Apple Watch®, or Android™ smartphone with Google Wallet™* using Disney MagicMobile service. Download the My Disney Experience app when you arrive at Walt Disney World® Resort and create a Disney MagicMobile pass**. The app is intended for use in the United States of America only. Currently, the app is only available in English.',
                    description: 'Description of Disney MagicMobile service and how to access tickets digitally on Disney Orlando voucher',
                  })}
                </BodyText>

                <BulletList>
                  <li>
                    <BodyText variant="small">
                      <FormattedMessage
                        id="Negu-RIp16qFI3IiUiyG"
                        defaultMessage="To create your Disney MagicMobile pass, select the menu icon in the lower-right corner of the <em>My Disney Experience</em> app’s home screen. Then, select Disney MagicMobile and follow the instructions to add your Disney MagicMobile pass to your digital wallet."
                        description="Bullet point instruction for creating a Disney MagicMobile pass on Disney Orlando voucher"
                        values={{ em: (chunks) => <em>{chunks}</em> }}
                      />
                    </BodyText>
                  </li>
                  <li>
                    <BodyText variant="small">
                      {intl.formatMessage({
                        id: 'Y2-S09RLZlgmoXPMXIIi',
                        defaultMessage: 'You should see your Disney MagicMobile pass in the Apple Wallet® app on iPhone® and Apple Watch® or in the Google Wallet™ app on Android™ smartphones.',
                        description: 'Bullet point about where to find Disney MagicMobile pass in digital wallets on Disney Orlando voucher',
                      })}
                    </BodyText>
                  </li>
                  <li>
                    <BodyText variant="small">
                      {intl.formatMessage({
                        id: 'AQruby8UgY_Vub_kOnKm',
                        defaultMessage: 'To enter a Theme Park, hold your mobile device containing your Disney MagicMobile pass near one of the touchpoints at a Theme Park entrance. Then, place your finger on the pad and watch for the green light.',
                        description: 'Bullet point instruction for entering Theme Park with Disney MagicMobile pass on Disney Orlando voucher',
                      })}
                    </BodyText>
                  </li>
                </BulletList>
              </InfoSection>
            </Group>

            <TicketWindowInstructionsRow>
              <Group direction="vertical" gap={8} horizontalAlign="start">
                <InfoSection>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage({
                      id: '46ql6zOJLIcKQCDSpzUB',
                      defaultMessage: 'Not using Disney MagicMobile Service? Visit a Ticket Window',
                      description: 'Heading for alternative ticket pickup section on Disney Orlando voucher',
                    })}
                  </BodyText>
                  <BodyText variant="small">
                    <FormattedMessage
                      id="BWSOsGmj6cyzprs0v11C"
                      defaultMessage="Alternatively, upon arrival, the person whose name is listed on this ticket confirmation can pick up your ticket order by presenting this ticket confirmation, along with a valid and properly matched government-issued photo ID (e.g., driver’s license, passport), at <em>Walt Disney World®</em> Theme Park ticket windows or the Disney Ticket Center at <em>Disney Springs®</em>. The order may not be released to anyone other than the person who is listed on this ticket confirmation."
                      description="Instructions for picking up tickets at a ticket window on Disney Orlando voucher"
                      values={{ em: (chunks) => <em>{chunks}</em> }}
                    />
                  </BodyText>
                </InfoSection>

                <BodyText variant="small">
                  {intl.formatMessage({
                    id: 'HxL3iLU_W2BZYYPri16G',
                    defaultMessage: '** Apple, the Apple logo, Apple Watch, Apple Wallet and iPhone, are trademarks of Apple Inc., registered in the U.S. and other countries. Android and Google Wallet are trademarks of Google Inc.',
                    description: 'Apple and Google trademark disclaimer on Disney Orlando voucher',
                  })}
                </BodyText>

                <BodyText variant="small">
                  {intl.formatMessage({
                    id: 'tUPo6nDTbp0oQeVZ6eWU',
                    defaultMessage: '*Message, data and roaming rates may apply. Availability subject to handset limitations, and features may vary by handset, service provider or otherwise. Coverage and app stores not available everywhere. Separate terms and conditions apply. If you’re under 18, get your parents’ permission first. Some features require separate theme park admission.',
                    description: 'Terms and conditions disclaimer for MagicMobile service on Disney Orlando voucher',
                  })}
                </BodyText>
              </Group>
              {/* Vertical Barcode alongside Instructions */}
              <BarcodePositionContainer>
                <RotatedBarcodeWrapper>
                  <Group direction="vertical" horizontalAlign="center" gap={8}>
                    <DisneyBarcodeContainer>
                      <BarCodeComponent width={2} height={48} format="CODE39" displayValue value={voucherCode.number} />
                    </DisneyBarcodeContainer>
                  </Group>
                </RotatedBarcodeWrapper>
              </BarcodePositionContainer>
            </TicketWindowInstructionsRow>

            {/* Legal Footer */}
            <Group direction="vertical" gap={4}>
              <BodyText variant="small">
                <FormattedMessage
                  id="a5vm98s_x4Qsbp8A_SEg"
                  defaultMessage="When you receive your ticket, it will provide:{br}NONREFUNDABLE • NOT FOR RESALE • VOID IF ALTERED • REVOCABLE • ADMIT ONE"
                  description="Legal notice on Disney Orlando voucher describing ticket terms"
                  values={{ br: <br /> }}
                />
              </BodyText>

              <BodyText variant="small">
                {intl.formatMessage({
                  id: 'unfSPJOlfAiisxO1X5bI',
                  defaultMessage: 'If this card includes admission rights, it is nontransferable. Disney is not responsible for lost or stolen tickets. Parks, attractions, or entertainment may change operating hours; close due to refurbishing, capacity, low demand, weather, special events or other reasons; and may otherwise change or be discontinued without notice and without liability to the owners of the Walt Disney World® Resort. If re-entry is permitted, biometric or other confirmation required with ticket for re-entry. Unless otherwise specifically states, not valid for special or premium events or other activities which are separately priced. Not valid for any park commencing operations after November 1, 2019. It is agreed between owners of the Walt Disney World® Resort and ticket users that all claims for injury or loss arising incident to presence on owners’ property shall be litigated in Orange County, Florida, under Florida law.',
                  description: 'Legal disclaimer about ticket terms and conditions on Disney Orlando voucher',
                })}
              </BodyText>
            </Group>
          </Group>
        </ContentContainer>
      </PageContainer>
    </LayoutContainer>
  )
}
